<template>
	<base-modal
		name="payoutForm"
		:title="$t('shop.payoutForm.title')"
		:subtitle="$t('shop.payoutForm.subtitle')"
		:save-text="$t('shop.payoutForm.button.submit')"
		:loading="loading"
		@submit="sendPayout"
	>
		<!-- Guthaben Card -->
		<v-card
			outlined
			class="mb-6"
			elevation="0"
		>
			<v-card-text class="text-center py-6">
				<div class="text-caption grey--text text-uppercase mb-2">{{ $t('shop.payoutForm.availableBalance') }}</div>
				<div class="primary--text text-h3 font-weight-bold mb-1">
					{{ availableBalance }} {{ currency }}
				</div>
				<div class="text-caption grey--text">{{ $t('shop.payoutForm.asOf') }}{{ formDate(new Date()) }}</div>
			</v-card-text>
		</v-card>

		<!-- Kostenübersicht -->
		<v-card
			outlined
			class="mb-6"
			elevation="0"
		>
			<v-card-title class="text-h6 font-weight-bold pb-2">
				{{ $t('shop.payoutForm.costOverview') }}
			</v-card-title>
			<v-divider></v-divider>
			<v-card-text class="pt-4">
				<v-row class="mb-2">
					<v-col cols="8">
						<div class="text-body-2">{{ $t('shop.payoutForm.commission') }}</div>
					</v-col>
					<v-col
						cols="4"
						class="text-right"
					>
						<div class="text-body-2 font-weight-medium">
							{{ (availableBalance * 0.2).toFixed(2) }} {{ currency }}
						</div>
					</v-col>
				</v-row>
				<v-divider class="my-3"></v-divider>
				<v-row>
					<v-col cols="8">
						<div class="text-body-1 font-weight-bold">{{ $t('shop.payoutForm.possiblePayout') }}</div>
					</v-col>
					<v-col
						cols="4"
						class="text-right"
					>
						<div class="text-body-1 font-weight-bold success--text">
							{{ maxPayoutAmount.toFixed(2) }} {{ currency }}
						</div>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>

		<!-- Auszahlungsbetrag -->
		<v-card
			outlined
			class="mb-6"
			elevation="0"
		>
			<v-card-title class="text-h6 font-weight-bold pb-2">
				{{ $t('shop.payoutForm.payoutAmount') }}
			</v-card-title>
			<v-divider></v-divider>
			<v-card-text class="pt-4">
				<v-text-field
					v-model="payoutAmount"
					:label="$t('shop.payoutForm.label.desiredAmount')"
					type="number"
					step="0.01"
					name="amount"
					:placeholder="$t('shop.payoutForm.placeholder.maxAmount')"
					:rules="amountRules"
					outlined
					dense
					:suffix="currency"
					:hint="$t('shop.payoutForm.hint.enterAmount')"
					persistent-hint
				/>
			</v-card-text>
		</v-card>

		<!-- Auszahlungsart -->
		<v-card
			outlined
			class="mb-6"
			elevation="0"
		>
			<v-card-title class="text-h6 font-weight-bold pb-2">
				{{ $t('shop.payoutForm.payoutType') }}
			</v-card-title>
			<v-divider></v-divider>
			<v-card-text class="pt-4">
				<!-- Auswahl Buttons -->
				<v-row class="mb-4">
					<v-col cols="6">
						<v-btn
							@click="tooglePaypalField"
							:outlined="!ppInfo"
							:color="ppInfo ? 'primary' : ''"
							block
							large
						>
							<v-icon left>mdi-paypal</v-icon>
							{{ $t('shop.payoutForm.button.paypal') }}
						</v-btn>
					</v-col>
					<v-col cols="6">
						<v-btn
							@click="toogleBankField"
							:outlined="!bankInfo"
							:color="bankInfo ? 'primary' : ''"
							block
							large
						>
							<v-icon left>mdi-bank</v-icon>
							{{ $t('shop.payoutForm.button.bankAccount') }}
						</v-btn>
					</v-col>
				</v-row>

				<!-- PayPal Felder -->
				<v-expand-transition>
					<div v-if="ppInfo">
						<v-divider class="mb-4"></v-divider>
						<v-text-field
							v-model="paypalEmail"
							:label="$t('shop.payoutForm.label.paypalEmail')"
							type="email"
							name="email"
							:placeholder="$t('shop.payoutForm.placeholder.email')"
							outlined
							dense
							:rules="eMailRules"
							prepend-inner-icon="mdi-email"
						/>
					</div>
				</v-expand-transition>

				<!-- Bank Felder -->
				<v-expand-transition>
					<div v-if="bankInfo">
						<v-divider class="mb-4"></v-divider>
						<v-text-field
							v-model="accountHolder"
							:label="$t('shop.payoutForm.label.accountHolder')"
							type="text"
							name="name"
							:placeholder="$t('shop.payoutForm.placeholder.accountHolder')"
							outlined
							dense
							:rules="accountHolderRules"
							prepend-inner-icon="mdi-account"
							class="mb-3"
						/>
						<v-text-field
							v-model="formattedIban"
							:label="$t('shop.payoutForm.label.iban')"
							name="iban"
							type="text"
							:placeholder="$t('shop.payoutForm.placeholder.iban')"
							maxlength="34"
							:rules="ibanRules"
							outlined
							dense
							prepend-inner-icon="mdi-credit-card"
							class="mb-3"
						/>
						<v-text-field
							v-model="bic"
							:label="$t('shop.payoutForm.label.bic')"
							type="text"
							name="bic"
							:placeholder="$t('shop.payoutForm.placeholder.bic')"
							outlined
							dense
							:rules="bicRules"
							prepend-inner-icon="mdi-bank-transfer"
							class="mb-3"
						/>
						<v-text-field
							v-model="bankName"
							:label="$t('shop.payoutForm.label.bankName')"
							type="text"
							name="bank"
							:placeholder="$t('shop.payoutForm.placeholder.bankName')"
							outlined
							dense
							:rules="bankNameRules"
							prepend-inner-icon="mdi-office-building"
						/>
					</div>
				</v-expand-transition>
			</v-card-text>
		</v-card>
	</base-modal>
</template>

<script>
import moment from 'moment';
import APIService from '@/services/api.service';

export default {
	name: 'payoutForm',

	components: {
		BaseModal: () => import('@/components/modal/baseModal')
	},

	data() {
		return {
			ppInfo: false,
			bankInfo: false,
			payoutAmount: '',
			paypalEmail: '',
			accountHolder: '',
			formattedIban: '',
			bic: '',
			bankName: '',
			loading: false,
			payoutDataValid: false,
			eMailRules: [
				v => !!v || this.$t('shop.payoutForm.validation.emailRequired'),
				v => !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || this.$t('shop.payoutForm.validation.emailFormat')
			],
			accountHolderRules: [
				v => !!v || this.$t('shop.payoutForm.validation.accountHolderRequired'),
				v => !v || v.trim().length >= 2 || this.$t('shop.payoutForm.validation.accountHolderMin'),
				v => !v || v.trim().length <= 100 || this.$t('shop.payoutForm.validation.accountHolderMax'),
				v => !v || /^[a-zA-ZäöüÄÖÜß\s\-\.]+$/.test(v) || this.$t('shop.payoutForm.validation.accountHolderFormat')
			],
			bicRules: [
				v => !!v || this.$t('shop.payoutForm.validation.bicRequired'),
				v => !v || /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(v.replace(/\s/g, '').toUpperCase()) || this.$t('shop.payoutForm.validation.bicFormat'),
				v => !v || (v.replace(/\s/g, '').length === 8 || v.replace(/\s/g, '').length === 11) || this.$t('shop.payoutForm.validation.bicLength')
			],
			bankNameRules: [
				v => !!v || this.$t('shop.payoutForm.validation.bankNameRequired'),
				v => !v || v.trim().length >= 2 || this.$t('shop.payoutForm.validation.bankNameMin'),
				v => !v || v.trim().length <= 100 || this.$t('shop.payoutForm.validation.bankNameMax')
			],
			ibanRules: [
				v => !!v || this.$t('shop.payoutForm.validation.ibanRequired'),
				v => !v || /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(v.replace(/\s/g, '')) || this.$t('shop.payoutForm.validation.ibanFormat'),
				v => !v || v.replace(/\s/g, '').length >= 15 || this.$t('shop.payoutForm.validation.ibanTooShort'),
				v => !v || v.replace(/\s/g, '').length <= 34 || this.$t('shop.payoutForm.validation.ibanTooLong'),
				v => {
					if (!v) return true;
					const iban = v.replace(/\s/g, '').toUpperCase();

					// Längen pro Land
					const lengths = {
						DE: 22, AT: 20, CH: 21, FR: 27, IT: 27, ES: 24,
						NL: 18, BE: 16, GB: 22, IE: 22, PT: 25, LU: 20
					};

					const country = iban.substring(0, 2);
					const expectedLength = lengths[country];

					if (!expectedLength) return this.$t('shop.payoutForm.validation.unknownCountryCode');
					if (iban.length !== expectedLength) return this.$t('shop.payoutForm.validation.ibanCountryLength', { country, expectedLength });

					// Mod-97 Prüfung
					const moved = iban.substring(4) + iban.substring(0, 4);
					const numeric = moved.replace(/[A-Z]/g, char => char.charCodeAt(0) - 55);

					let remainder = 0;
					for (let i = 0; i < numeric.length; i++) {
						remainder = (remainder * 10 + parseInt(numeric[i])) % 97;
					}

					return remainder === 1 || this.$t('shop.payoutForm.validation.ibanChecksum');
				}
			],
		};
	},

	computed: {
		availableBalance() {
			return this.$store.state?.main?.creatorRevenue?.available_balance || 0;
		},

		currency() {
			return this.$store.state?.main?.creatorRevenue?.currency || 'EUR';
		},

		maxPayoutAmount() {
			return this.availableBalance * 0.8;
		},

		amountRules() {
			return [
				v => !!v || this.$t('shop.payoutForm.validation.amountRequired'),
				v => !isNaN(parseFloat(v)) || this.$t('shop.payoutForm.validation.amountFormat'),
				v => parseFloat(v) > 0 || this.$t('shop.payoutForm.validation.amountPositive'),
				v => parseFloat(v) <= this.maxPayoutAmount || this.$t('shop.payoutForm.validation.amountMax', { max: this.maxPayoutAmount.toFixed(2), currency: this.currency })
			];
		}
	},

	methods: {
		async sendPayout(e) {
			e.preventDefault();
			this.loading = true;

			try {
				const payload = APIService.getFormData(APIService.getForm(e));

				// Typ setzen
				payload.type = this.ppInfo ? 'paypal' : 'bank_transfer';

				// Amount als Number
				payload.amount = parseFloat(this.payoutAmount);

				await this.$store.dispatch('post', {
					endpoint: '/api/v1/creator/payout/request',
					event: payload,
					state: 'payouts',
					message: false,
				});

				// Revenue und Payouts neu laden
				await Promise.all([
					this.$store.dispatch('fetch', {
						endpoint: '/api/v1/creator/revenue',
						state: 'creatorRevenue'
					}),
					this.$store.dispatch('fetch', {
						endpoint: '/api/v1/creator/payout/list',
						state: 'payouts'
					})
				]);

				this.close('payoutForm');
				this.resetForm();
			} catch (error) {
				console.log("Error auf payoutForm", error.response.data.error)
				APIService.setResponse({
					message: `${error.response.data.error}`,
					color: "red"
				})
				console.error('Payout request failed:', error);
			} finally {
				this.loading = false;
			}
		},

		resetForm() {
			this.payoutAmount = '';
			this.paypalEmail = '';
			this.accountHolder = '';
			this.formattedIban = '';
			this.bic = '';
			this.bankName = '';
			this.ppInfo = false;
			this.bankInfo = false;
		},

		tooglePaypalField() {
			this.bankInfo = !this.bankInfo
			this.ppInfo = !this.ppInfo
			if (this.bankInfo) {
				this.bankInfo = !this.bankInfo
			}
		},

		toogleBankField() {
			this.bankInfo = !this.bankInfo
			if (this.ppInfo) {
				this.ppInfo = !this.ppInfo
			}
		},

		countTransactions() {
			const orders = this.$store.state.main?.orders?.count || []
			const subs = this.$store.state?.main?.subscriptions?.count || []
			var amount = orders + subs
			return amount
		},

		calcTransactionsCost() {
			const transActions = this.countTransactions();
			return transActions * 0.5;
		},
		currentUser() {
			return this.$store.state.auth.status.loggedIn;
		},

		formDate(value) {
			if (value) {
				return moment(String(value)).format('DD.MM.YYYY')
			}
		},

		close(name) {
			this.$store.dispatch("modal/close", name)
		}
	}

	// Ende Methods
}
</script>
